import { Flex, Paper, Text, Badge, Box } from "@mantine/core";
import ReadMoreText from "@/components/shared/ReadMoreText";
import {
  IconBuilding,
  IconMapPin,
  IconTool,
  IconShieldCheck,
  IconBriefcase,
  IconTarget,
  IconUserCheck,
  IconUser,
} from "@tabler/icons-react";
import ProductsServicesTab from "./ProductsServicesTab";
import {
  getCollaborationIntentLabel,
  getCollaborationRoleLabel,
  getWhoAmILabel,
  getIndustryLabel,
  getSubIndustryLabel,
  getAdditionalTechInfraLabel,
  regionsData,
} from "../constants/general.const";

interface ProfileTabProps {
  company: any;
  collaboration?: any;
}

export default function ProfileTab({ company, collaboration }: ProfileTabProps) {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };
  const metadata = company.externalVerificationLogs?.[0]?.metadata;

  return (
    <>
      {/* Collaboration Intent Details */}
      {collaboration?.requesterRole && (
        <Paper p={{ base: "md", sm: "xl" }} radius="md" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
          <Flex align="center" gap="xs" mb={4}>
            <Text size="lg" fw={400}>
              Collaboration Intent Details
            </Text>
          </Flex>
          <Text size="xs" c="slate.5" mb="lg">
            Information provided by the company about their collaboration goals
          </Text>

          <Flex direction="column" gap="md">
            {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0 && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconTarget size={16} color="var(--mantine-color-primary-6)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    Collaboration type they're exploring
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  {company.collaborationIntent.map((intent: string, i: number) => (
                    <Badge
                      key={i}
                      variant="light"
                      color="#E9D4FF"
                      bg="#FAF5FF"
                      c="#8200DB"
                      py={10}
                      px={8}
                      size="md"
                      fw={400}
                      style={{ border: "1px solid #E9D4FF" }}
                    >
                      {getCollaborationIntentLabel(intent)}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            <Box>
              <Flex align="center" gap={6} mb="xs">
                <IconBriefcase size={16} color="var(--mantine-color-primary-6)" />
                <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                  Their role in this collaboration
                </Text>
              </Flex>
              <Flex gap="xs" wrap="wrap">
                <Badge
                  variant="light"
                  color="#B9F8CF"
                  bg="#F0FDF4"
                  c="#008236"
                  py={10}
                  px={8}
                  size="md"
                  fw={400}
                  style={{ border: "1px solid #B9F8CF" }}
                >
                  {getCollaborationRoleLabel(collaboration.requesterRole)}
                </Badge>
              </Flex>
            </Box>

            {collaboration.expectedPartnerRole && (
              <Box>
                <Flex align="center" gap={6} mb="xs">
                  <IconUserCheck size={16} color="var(--mantine-color-primary-6)" />
                  <Text size="xs" c="slate.6" tt="uppercase" fw={400}>
                    What they expect from you
                  </Text>
                </Flex>
                <Flex gap="xs" wrap="wrap">
                  <Badge
                    variant="light"
                    color="#FEE685"
                    bg="#FFFBEB"
                    c="#BB4D00"
                    py={10}
                    px={8}
                    size="md"
                    fw={400}
                    style={{ border: "1px solid #FEE685" }}
                  >
                    {getCollaborationRoleLabel(collaboration.expectedPartnerRole)}
                  </Badge>
                </Flex>
              </Box>
            )}
          </Flex>
        </Paper>
      )}

      {/* Business Overview */}
      <Paper p={{ base: "md", sm: "xl" }} radius="md" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconBuilding size={18} color="#2563EB" />
          <Text size="lg" fw={400}>
            Business Overview
          </Text>
        </Flex>

        <Flex gap="xl" wrap="wrap">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              COMPANY DESCRIPTION
            </Text>
            <ReadMoreText text={company.description || ""} />
          </Flex>

          {/* <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              YEARS OF OPERATION
            </Text>
            <Text size="sm">{getYearsOfOperation()}</Text>
          </Flex> */}

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              TRADE NAME
            </Text>
            <Text size="sm">XXXX</Text>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              INDUSTRY CATEGORY
            </Text>
            <Text size="sm">
              {Array.isArray(company.industryCategory) && company.industryCategory.length > 0
                ? company.industryCategory.map((c: string) => getIndustryLabel(c)).join(", ")
                : "N/A"}
            </Text>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              SUB INDUSTRY
            </Text>
            <Text size="sm">
              {Array.isArray(company.subIndustry) && company.subIndustry.length > 0
                ? company.subIndustry.map((sub: string) => getSubIndustryLabel(sub)).join(", ")
                : "N/A"}
            </Text>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              COLLABORATION INTENT
            </Text>
            <Text size="sm">
              {Array.isArray(company.collaborationIntent) && company.collaborationIntent.length > 0
                ? company.collaborationIntent.map((intent: string) => getCollaborationIntentLabel(intent)).join(", ")
                : "N/A"}
            </Text>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="xs">
              WHO AM I
            </Text>
            <Text size="sm">
              {Array.isArray(company.whoAmI) && company.whoAmI.length > 0
                ? company.whoAmI.map((role: string) => getWhoAmILabel(role)).join(", ")
                : "N/A"}
            </Text>
          </Flex>
        </Flex>
      </Paper>

      {/* Products & Services */}
      <ProductsServicesTab company={company} />

      {/* Capabilities & Offerings */}
      <Paper p={{ base: "md", sm: "xl" }} radius="md" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconTool size={18} color="var(--mantine-color-primary-6)" style={{ transform: "scaleX(-1)" }} />
          <Text size="lg" fw={400}>
            Capabilities & Offerings
          </Text>
        </Flex>

        <Flex gap="xl" wrap="wrap" mb="lg">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
              CORE CAPABILITIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.coreCapabilities || []).map((capability: string, index: number) => (
                <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                  {capability}
                </Badge>
              ))}
              {(!company.coreCapabilities || company.coreCapabilities.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>

          {company.secondaryCapabilities && company.secondaryCapabilities.length > 0 && (
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                SECONDARY CAPABILITIES
              </Text>
              <Flex gap="xs" wrap="wrap">
                {company.secondaryCapabilities.map((capability: string, index: number) => (
                  <Badge key={index} variant="light" color="slate.6" c="black" py={10} px={8} size="md" fw={400}>
                    {capability}
                  </Badge>
                ))}
              </Flex>
            </Flex>
          )}
        </Flex>

        {company.keyDifferentiators && (
          <Flex direction="column" mb="lg">
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
              KEY DIFFERENTIATORS
            </Text>
            <Text size="sm">{company.keyDifferentiators}</Text>
          </Flex>
        )}

        <Flex direction="column" mb="lg">
          <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
            TECHNOLOGY & INFRASTRUCTURE
          </Text>
          <Flex gap="xs" wrap="wrap">
            {(company.technologies || []).map((tech: string, index: number) => (
              <Badge key={index} variant="light" color="slate" c="black" py={10} px={8} size="md" fw={400}>
                {tech}
              </Badge>
            ))}
            {(!company.technologies || company.technologies.length === 0) && (
              <Text size="sm" c="slate.6">
                N/A
              </Text>
            )}
          </Flex>
        </Flex>

        {company.additionalTechInfrastructure && company.additionalTechInfrastructure.length > 0 && (
          <Flex direction="column">
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
              ADDITIONAL TECHNOLOGY & INFRASTRUCTURE
            </Text>
            <Flex gap="xs" wrap="wrap">
              {company.additionalTechInfrastructure.map((item: string, index: number) => (
                <Badge key={index} variant="light" color="slate" c="black" py={10} px={8} size="md" fw={400}>
                  {getAdditionalTechInfraLabel(item)}
                </Badge>
              ))}
            </Flex>
          </Flex>
        )}
      </Paper>

      {/* Geography & Markets */}
      <Paper p={{ base: "md", sm: "xl" }} radius="md" mb="xl" style={{ border: "1px solid #E5E7EB" }}>
        <Flex align="center" gap="xs" mb="md">
          <IconMapPin size={18} color="#2563EB" />
          <Text size="lg" fw={400}>
            Geography & Markets
          </Text>
        </Flex>

        <Flex gap="xl" wrap="wrap">
          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
              HEADQUARTERS
            </Text>
            <Text size="sm">
              {company.headquartersCity && company.headquartersCountry
                ? `${company.headquartersCity}, ${company.headquartersCountry}`
                : company.headquartersCountry || "N/A"}
            </Text>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
              OPERATING COUNTRIES
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.operatingCountries || []).map((country: string, index: number) => (
                <Badge key={index} variant="light" color="slate" c="black" py={10} px={8} size="md" fw={400}>
                  {country}
                </Badge>
              ))}
              {(!company.operatingCountries || company.operatingCountries.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>

          <Flex direction="column" style={{ flex: "1 1 100%" }}>
            <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
              PREFERRED COLLABORATION REGIONS
            </Text>
            <Flex gap="xs" wrap="wrap">
              {(company.preferredCollaborationRegions || []).map((region: string, index: number) => (
                <Badge key={index} variant="light" color="slate" c="black" py={10} px={8} size="md" fw={400}>
                  {getRegionLabel(region)}
                </Badge>
              ))}
              {(!company.preferredCollaborationRegions || company.preferredCollaborationRegions.length === 0) && (
                <Text size="sm" c="slate.6">
                  N/A
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Paper>

      {/* Contact Person - masked */}
      {(() => {
        const mandatoryDocs = (company.documents || []).filter((d: any) =>
          ["AFFIDAVIT", "AUTHORITY_LETTER"].includes(d.category)
        );
        const categoryLabel = (cat: string) =>
          cat === "AUTHORITY_LETTER" ? "Authority Letter" : "Affidavit";
        return (
          <Paper p={{ base: "md", sm: "xl" }} radius="lg" mt="xl" style={{ border: "1px solid #E5E7EB" }}>
            <Flex align="center" gap="xs" mb="md">
              <IconUser size={18} color="#2563EB" />
              <Text size="lg" fw={400}>
                Contact Person
              </Text>
            </Flex>

            <Flex direction="column" gap="lg">
              <Flex gap="xl" wrap="wrap">
                <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                  <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>NAME</Text>
                  <Text size="sm">XXXX</Text>
                </Flex>
                <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                  <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>DESIGNATION</Text>
                  <Text size="sm">XXXX</Text>
                </Flex>
                <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                  <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>PHONE NUMBER</Text>
                  <Text size="sm">XXXX</Text>
                </Flex>
                {mandatoryDocs.map((doc: any) => (
                  <Flex key={doc.id} direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
                    <Text size="xs" c="slate.6" tt="uppercase" mb="xs" fw={400}>
                      {categoryLabel(doc.category)}
                    </Text>
                    <Text size="sm">
                      {doc.createdAt
                        ? `Uploaded on ${new Date(doc.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`
                        : "—"}
                    </Text>
                  </Flex>
                ))}
              </Flex>
            </Flex>
          </Paper>
        );
      })()}

      {/* System Verification Details */}
      {metadata && (
        <Paper p={{ base: "md", sm: "xl" }} radius="md" mt="xl" style={{ border: "1px solid #E5E7EB" }}>
          <Flex align="center" gap="xs" mb="md">
            <IconShieldCheck size={18} color="#2563EB" />
            <Text size="lg" fw={400}>
              System Verification Details
            </Text>
          </Flex>

          <Flex gap="xl" wrap="wrap">
            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                COMPANY NAME
              </Text>
              <Text size="sm">{metadata.companyName ? "XXXX" : "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                COMPANY ADDRESS
              </Text>
              <Text size="sm">{metadata.companyAddress ? "XXXX" : "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                DATE OF INCORPORATION
              </Text>
              <Text size="sm">{metadata.dateOfIncorporation ? "XXXX" : "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                REGISTRATION NUMBER
              </Text>
              <Text size="sm">{metadata.registrationNumber ? "XXXX" : "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                COMPANY TYPE
              </Text>
              <Text size="sm">{metadata.companyType ? "XXXX" : "-"}</Text>
            </Flex>

            <Flex direction="column" style={{ flex: "1 1 45%", minWidth: "280px" }}>
              <Text size="xs" c="slate.6" tt="uppercase" mb="sm">
                COMPANY STATUS
              </Text>
              <Text size="sm">{metadata.companyStatus ? "XXXX" : "-"}</Text>
            </Flex>
          </Flex>
        </Paper>
      )}
    </>
  );
}
